import { v4 as uuid } from '@lukeed/uuid'
import type { AppContext } from 'types/common'
import { graphql, type StoreConfigurationPageLoad$result } from '$houdini'
import type { Merge, WritableDeep } from 'type-fest'
import { getSession } from 'stores/session'
import { checkSessionPermission } from 'utility/permission'

import svelteComponent from './Store.svelte'

// Houdini result types are readonly, but the store screen edits these in place
type StoreResult = WritableDeep<StoreConfigurationPageLoad$result['stores'][number]>

/** A store from the API, reshaped for SiteDetails (`street` instead of `address`, non-null `phone`), plus UI state */
export type Store = Merge<
	Omit<StoreResult, 'address'>,
	{
		street: string
		phone: string
		uuid: string
		deleted: boolean
		dirty: boolean
		remittanceIsMainAddress: boolean
	}
>

export interface State {
	abbreviation: string
	name: string
}

export default function ({ stateRouter }: AppContext) {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
	stateRouter.addState({
		name: 'app.configuration.store',
		route: 'store',
		querystringParameters: ['lastResetTime', 'lastSavedTime'],
		defaultParameters: {
			lastResetTime: null,
			lastSavedTime: null,
		},
		template: {
			svelte: true, // this tells our renderer wrapper that this is a svelte component
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			component: svelteComponent, // this is the svelte component
			options: {
				// these are the options passed to the svelte component
			},
		},
		canLeaveState(domApi) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return domApi.canLeaveState()
		},
		async resolve(_data, _parameters) {
			const { data } = await storeConfigurationQuery.fetch()
			const session = getSession()
			const loggedInPlant = session.currentStore ?? null
			const filteredStores: Store[] = (data?.stores ?? []).reduce((acc: Store[], store) => {
				if (store.private && store.id !== loggedInPlant) {
					return acc
				}
				let remittanceIsMainAddress = false
				if (
					store.remittanceAddress === store.address &&
					store.remittanceCity === store.city &&
					store.remittanceState === store.state &&
					store.remittanceZip === store.zip &&
					store.remittanceCountry === store.country &&
					store.remittanceAddress !== '' &&
					store.remittanceCity !== '' &&
					store.remittanceState !== '' &&
					store.remittanceZip !== '' &&
					store.remittanceCountry !== ''
				) {
					remittanceIsMainAddress = true
				}
				acc.push({
					...store,
					remittanceIsMainAddress,
					private: store.private,
					street: store.address,
					// SiteDetails requires a string here, but the API allows null
					phone: store.phone ?? '',
					uuid: uuid(),
					deleted: false,
					dirty: false,
				})
				return acc
			}, [])

			const states: State[] = (data?.states ?? []).map(state => {
				return {
					abbreviation: state.stateAbbreviation ?? '',
					name: state.stateName ?? '',
				}
			})

			const selectedStoreUuid = filteredStores[0].uuid
			const userStores = session.stores ?? []
			return {
				canConfigureStores: checkSessionPermission('CONFIGURATION_CAN_CONFIGURE_STORES'),
				selectedStoreUuid,
				states,
				stores: filteredStores,
				userStores,
			}
		},
	})
}

const storeConfigurationQuery = graphql(`
	query StoreConfigurationPageLoad {
		stores {
			id
			address
			city
			code
			country
			fax
			invoiceMemo
			name
			phone
			private
			remittanceAddress
			remittanceCity
			remittanceCountry
			remittancePhone
			remittanceState
			remittanceZip
			state
			webAddress
			zip
			addressService {
				service
				apiKey
			}
			taxService {
				apiKey
				service
			}
		}
		states {
			stateAbbreviation
			stateName
		}
	}
`)
